import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useWorkspace } from '../contexts/WorkspaceContext';
import apiClient from '../config/axios';
import toast from 'react-hot-toast';
import {
    Plus, Edit, Trash2, TestTube, CheckCircle, XCircle, Eye, EyeOff, Loader2,
    Mail, Server, Activity, Settings
} from 'lucide-react';

const SMTPManagement = () => {
    const { currentWorkspace } = useWorkspace();
    const navigate = useNavigate();

    const [smtpList, setSmtpList] = useState([]);
    const [loading, setLoading] = useState(true);
    const [testingId, setTestingId] = useState(null);
    const [showModal, setShowModal] = useState(false);
    const [showPassword, setShowPassword] = useState({});
    const [showModalPassword, setShowModalPassword] = useState(false);
    const [editingId, setEditingId] = useState(null);
    const [submitting, setSubmitting] = useState(false);
    const [formData, setFormData] = useState({
        name: '',
        smtp_host: '',
        smtp_port: '587',
        smtp_user: '',
        smtp_password: '',
        from_email: '',
        from_name: '',
        daily_email_limit: 100,
        is_active: true
    });

    // Load SMTP list
    useEffect(() => {
        loadSMTPList();
    }, [currentWorkspace]);

    const loadSMTPList = async () => {
        try {
            setLoading(true);
            const headers = currentWorkspace?.id ? { 'x-workspace-id': currentWorkspace.id } : {};
            const response = await apiClient.get('/api/settings/smtp', { headers });
            setSmtpList(response.data.smtp_list || []);
        } catch (error) {
            toast.error('Failed to load SMTP configurations');
        } finally {
            setLoading(false);
        }
    };

    const handleInputChange = (field, value) => {
        setFormData(prev => ({ ...prev, [field]: value }));
    };

    const resetForm = () => {
        setFormData({
            name: '',
            smtp_host: '',
            smtp_port: '587',
            smtp_user: '',
            smtp_password: '',
            from_email: '',
            from_name: '',
            daily_email_limit: 100,
            is_active: true
        });
        setEditingId(null);
        setShowModal(false);
        setShowModalPassword(false);
    };

    const handleCreate = async (e) => {
        e.preventDefault();
        try {
            setSubmitting(true);
            const headers = currentWorkspace?.id ? { 'x-workspace-id': currentWorkspace.id } : {};

            if (editingId) {
                await apiClient.put(`/api/settings/smtp/${editingId}`, formData, { headers });
                toast.success('SMTP configuration updated successfully');
            } else {
                await apiClient.post('/api/settings/smtp', formData, { headers });
                toast.success('SMTP configuration created successfully');
            }

            resetForm();
            loadSMTPList();
        } catch (error) {
            toast.error(error.response?.data?.error || 'Failed to save SMTP configuration');
        } finally {
            setSubmitting(false);
        }
    };

    const handleEdit = (smtp) => {
        setFormData({
            name: smtp.name || '',
            smtp_host: smtp.smtp_host || '',
            smtp_port: smtp.smtp_port?.toString() || '587',
            smtp_user: smtp.smtp_user || '',
            smtp_password: '', // Don't pre-fill password for security
            from_email: smtp.from_email || '',
            from_name: smtp.from_name || '',
            daily_email_limit: smtp.daily_email_limit || 100,
            is_active: smtp.status === 'active'
        });
        setEditingId(smtp.id);
        setShowModal(true);
    };

    const handleDelete = async (id) => {
        if (!window.confirm('Are you sure you want to delete this SMTP configuration?')) return;

        try {
            const headers = currentWorkspace?.id ? { 'x-workspace-id': currentWorkspace.id } : {};
            await apiClient.delete(`/api/settings/smtp/${id}`, { headers });
            toast.success('SMTP configuration deleted successfully');
            loadSMTPList();
        } catch (error) {
            toast.error(error.response?.data?.error || 'Failed to delete SMTP configuration');
        }
    };

    const handleTest = async (id) => {
        try {
            setTestingId(id);
            const headers = currentWorkspace?.id ? { 'x-workspace-id': currentWorkspace.id } : {};
            await apiClient.post(`/api/settings/smtp/${id}/test`, {}, { headers });
            toast.success('SMTP connection successful!');
        } catch (error) {
            toast.error(error.response?.data?.error || 'SMTP connection failed');
        } finally {
            setTestingId(null);
        }
    };

    const togglePasswordVisibility = (id) => {
        setShowPassword(prev => ({ ...prev, [id]: !prev[id] }));
    };

    const handleToggleStatus = async (id, currentStatus) => {
        try {
            const headers = currentWorkspace?.id ? { 'x-workspace-id': currentWorkspace.id } : {};
            const newStatus = currentStatus === 'active' ? 'inactive' : 'active';

            await apiClient.put(`/api/settings/smtp/${id}`, { status: newStatus }, { headers });
            toast.success(`SMTP ${newStatus === 'active' ? 'activated' : 'deactivated'} successfully`);
            loadSMTPList();
        } catch (error) {
            toast.error(error.response?.data?.error || 'Failed to update SMTP status');
        }
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center h-screen">
                <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
            </div>
        );
    }

    return (
        <div>
            {/* SMTP Configuration Dashboard */}
            <div className="bg-white rounded-xl shadow-sm border">
                {/* Header Section */}
                <div className="p-6 border-b border-gray-200">
                    <div className="flex items-center justify-between mb-6">
                        <div>
                            <h1 className="text-3xl font-bold text-gray-900">Email SMTP Configuration</h1>
                            <p className="text-gray-600 mt-2">Manage multiple SMTP server configurations</p>
                        </div>
                        <button
                            onClick={() => {
                                resetForm();
                                setShowModal(true);
                            }}
                            className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                        >
                            <Plus className="h-5 w-5" />
                            <span>Add New SMTP</span>
                        </button>
                    </div>

                    {/* Stats Cards */}
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                        <div className="bg-gray-50 p-4 rounded-lg">
                            <div className="flex items-center justify-between">
                                <div>
                                    <p className="text-sm font-medium text-gray-600">Total SMTP</p>
                                    <p className="text-xl font-bold text-gray-900">{smtpList.length}</p>
                                </div>
                                <Server className="h-6 w-6 text-blue-600" />
                            </div>
                        </div>
                        <div className="bg-gray-50 p-4 rounded-lg">
                            <div className="flex items-center justify-between">
                                <div>
                                    <p className="text-sm font-medium text-gray-600">Active</p>
                                    <p className="text-xl font-bold text-green-600">
                                        {smtpList.filter(s => s.status === 'active').length}
                                    </p>
                                </div>
                                <Activity className="h-6 w-6 text-green-600" />
                            </div>
                        </div>
                        <div className="bg-gray-50 p-4 rounded-lg">
                            <div className="flex items-center justify-between">
                                <div>
                                    <p className="text-sm font-medium text-gray-600">Inactive</p>
                                    <p className="text-xl font-bold text-gray-600">
                                        {smtpList.filter(s => s.status === 'inactive').length}
                                    </p>
                                </div>
                                <Settings className="h-6 w-6 text-gray-600" />
                            </div>
                        </div>
                        <div className="bg-gray-50 p-4 rounded-lg">
                            <div className="flex items-center justify-between">
                                <div>
                                    <p className="text-sm font-medium text-gray-600">Default</p>
                                    <p className="text-xl font-bold text-blue-600">
                                        {smtpList.filter(s => s.is_default).length}
                                    </p>
                                </div>
                                <Mail className="h-6 w-6 text-blue-600" />
                            </div>
                        </div>
                    </div>
                </div>

                {/* Table Section */}
                <div className="p-6">
                    <h2 className="text-xl font-semibold text-gray-900 mb-4">Your SMTP Servers</h2>

                    {smtpList.length === 0 ? (
                        <div className="p-8 text-center">
                            <Mail className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                            <h3 className="text-lg font-medium text-gray-900 mb-2">No SMTP configurations yet</h3>
                            <p className="text-gray-600 mb-4">
                                Create your first SMTP configuration to start sending emails
                            </p>
                            <button
                                onClick={() => {
                                    resetForm();
                                    setShowModal(true);
                                }}
                                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                            >
                                Create Your First SMTP
                            </button>
                        </div>
                    ) : (
                        <div className="overflow-x-auto">
                            <table className="w-full">
                                <thead className="bg-gray-50">
                                    <tr>
                                        <th className="px-6 py-3 text-left text-sm font-medium text-gray-700">Name</th>
                                        <th className="px-6 py-3 text-left text-sm font-medium text-gray-700">Host</th>
                                        <th className="px-6 py-3 text-left text-sm font-medium text-gray-700">Port</th>
                                        <th className="px-6 py-3 text-left text-sm font-medium text-gray-700">Username</th>
                                        <th className="px-6 py-3 text-left text-sm font-medium text-gray-700">From Email</th>
                                        <th className="px-6 py-3 text-left text-sm font-medium text-gray-700">Daily Limit</th>
                                        <th className="px-6 py-3 text-left text-sm font-medium text-gray-700">Status</th>
                                        <th className="px-6 py-3 text-right text-sm font-medium text-gray-700">Actions</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-200">
                                    {smtpList.map((smtp) => (
                                        <tr key={smtp.id} className="hover:bg-gray-50">
                                            <td className="px-6 py-4">
                                                <div className="flex items-center space-x-2">
                                                    <span className="text-sm font-medium text-gray-900">{smtp.name}</span>
                                                    {smtp.is_default && (
                                                        <span className="px-2 py-0.5 text-xs font-semibold rounded-full bg-blue-100 text-blue-800">
                                                            Default
                                                        </span>
                                                    )}
                                                </div>
                                            </td>
                                            <td className="px-6 py-4 text-sm text-gray-600">{smtp.smtp_host}</td>
                                            <td className="px-6 py-4 text-sm text-gray-600">{smtp.smtp_port}</td>
                                            <td className="px-6 py-4 text-sm text-gray-600">
                                                <div className="flex items-center space-x-2">
                                                    <span>{smtp.smtp_user}</span>
                                                    <button
                                                        onClick={() => togglePasswordVisibility(smtp.id)}
                                                        className="text-gray-400 hover:text-gray-600"
                                                    >
                                                        {showPassword[smtp.id] ? (
                                                            <EyeOff className="h-4 w-4" />
                                                        ) : (
                                                            <Eye className="h-4 w-4" />
                                                        )}
                                                    </button>
                                                </div>
                                            </td>
                                            <td className="px-6 py-4 text-sm text-gray-600">{smtp.from_email}</td>
                                            <td className="px-6 py-4 text-sm text-gray-600">
                                                <span className="px-2 py-1 text-xs font-medium rounded-full bg-blue-50 text-blue-700">
                                                    {smtp.daily_email_limit || 100}
                                                </span>
                                            </td>
                                            <td className="px-6 py-4">
                                                <button
                                                    onClick={() => handleToggleStatus(smtp.id, smtp.status)}
                                                    className={`px-2 py-1 text-xs rounded-full flex items-center space-x-1 transition-colors ${smtp.status === 'active'
                                                        ? 'bg-green-100 text-green-800 hover:bg-green-200'
                                                        : 'bg-gray-100 text-gray-800 hover:bg-gray-200'
                                                        }`}
                                                    title={`Click to ${smtp.status === 'active' ? 'deactivate' : 'activate'}`}
                                                >
                                                    {smtp.status === 'active' ? (
                                                        <>
                                                            <CheckCircle className="h-3 w-3" />
                                                            <span>Active</span>
                                                        </>
                                                    ) : (
                                                        <>
                                                            <XCircle className="h-3 w-3" />
                                                            <span>Inactive</span>
                                                        </>
                                                    )}
                                                </button>
                                            </td>
                                            <td className="px-6 py-4 text-right">
                                                <div className="flex items-center justify-end space-x-2">
                                                    <button
                                                        onClick={() => handleTest(smtp.id)}
                                                        disabled={testingId === smtp.id}
                                                        className="p-2 text-yellow-600 hover:bg-yellow-50 rounded-lg transition-colors disabled:opacity-50"
                                                        title="Test Connection"
                                                    >
                                                        {testingId === smtp.id ? (
                                                            <Loader2 className="h-4 w-4 animate-spin" />
                                                        ) : (
                                                            <TestTube className="h-4 w-4" />
                                                        )}
                                                    </button>
                                                    <button
                                                        onClick={() => handleEdit(smtp)}
                                                        className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                                                        title="Edit"
                                                    >
                                                        <Edit className="h-4 w-4" />
                                                    </button>
                                                    <button
                                                        onClick={() => handleDelete(smtp.id)}
                                                        className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                                                        title="Delete"
                                                    >
                                                        <Trash2 className="h-4 w-4" />
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            </div>

            {/* Create/Edit Modal */}
            {showModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
                        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
                            <h2 className="text-xl font-heading font-bold text-gray-900">
                                {editingId ? 'Edit SMTP Configuration' : 'Create New SMTP Configuration'}
                            </h2>
                            <button
                                onClick={resetForm}
                                className="text-gray-400 hover:text-gray-600 transition-colors"
                            >
                                ✕
                            </button>
                        </div>

                        <form onSubmit={handleCreate} className="p-6 space-y-4">
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        SMTP Name *
                                    </label>
                                    <input
                                        type="text"
                                        value={formData.name}
                                        onChange={(e) => handleInputChange('name', e.target.value)}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                                        placeholder="e.g., Primary SMTP"
                                        required
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        SMTP Host *
                                    </label>
                                    <input
                                        type="text"
                                        value={formData.smtp_host}
                                        onChange={(e) => handleInputChange('smtp_host', e.target.value)}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                                        placeholder="e.g., smtp.gmail.com"
                                        required
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        SMTP Port *
                                    </label>
                                    <input
                                        type="number"
                                        value={formData.smtp_port}
                                        onChange={(e) => handleInputChange('smtp_port', e.target.value)}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                                        placeholder="587"
                                        required
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        SMTP Username *
                                    </label>
                                    <input
                                        type="email"
                                        value={formData.smtp_user}
                                        onChange={(e) => handleInputChange('smtp_user', e.target.value)}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                                        placeholder="your-email@gmail.com"
                                        required
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        SMTP Password *
                                    </label>
                                    <div className="relative">
                                        <input
                                            type={showModalPassword ? "text" : "password"}
                                            value={formData.smtp_password}
                                            onChange={(e) => handleInputChange('smtp_password', e.target.value)}
                                            className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                                            placeholder="Enter SMTP password"
                                            required
                                        />
                                        <button
                                            type="button"
                                            onClick={() => setShowModalPassword(!showModalPassword)}
                                            className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600"
                                        >
                                            {showModalPassword ? (
                                                <EyeOff className="h-4 w-4" />
                                            ) : (
                                                <Eye className="h-4 w-4" />
                                            )}
                                        </button>
                                    </div>
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        From Email *
                                    </label>
                                    <input
                                        type="email"
                                        value={formData.from_email}
                                        onChange={(e) => handleInputChange('from_email', e.target.value)}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                                        placeholder="noreply@yourcompany.com"
                                        required
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        From Name
                                    </label>
                                    <input
                                        type="text"
                                        value={formData.from_name}
                                        onChange={(e) => handleInputChange('from_name', e.target.value)}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                                        placeholder="Your Company Name"
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Daily Email Limit *
                                    </label>
                                    <input
                                        type="number"
                                        value={formData.daily_email_limit}
                                        onChange={(e) => handleInputChange('daily_email_limit', parseInt(e.target.value) || 100)}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                                        placeholder="100"
                                        min="1"
                                        required
                                    />
                                    <p className="text-xs text-gray-500 mt-1">Maximum emails this SMTP can send per day</p>
                                </div>

                                <div className="col-span-2 flex items-center">
                                    <label className="flex items-center">
                                        <input
                                            type="checkbox"
                                            checked={formData.is_active}
                                            onChange={(e) => handleInputChange('is_active', e.target.checked)}
                                            className="mr-2 h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                                        />
                                        <span className="text-sm text-gray-700">Active</span>
                                    </label>
                                </div>
                            </div>

                            <div className="flex space-x-3 pt-4 border-t border-gray-200">
                                <button
                                    type="button"
                                    onClick={resetForm}
                                    className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    disabled={submitting}
                                    className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2"
                                >
                                    {submitting && <Loader2 className="h-4 w-4 animate-spin" />}
                                    <span>{editingId ? 'Update' : 'Create'} SMTP</span>
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

export default SMTPManagement;
